import type { EdgeClientFlags } from './edge-client-flags';
import type { ExperimentPolicy } from './flag-types';

/**
 * Server-only half of every edge experiment's definition: which identity keeps
 * the variant stable, and where its exposure event is allowed to fire.
 *
 * **Its own module because the CloudFront viewer-request bundle must never
 * reach it.** rolldown cannot tree-shake object properties, so anything
 * written onto an `EdgeClientFlags` entry ships to the edge whether or not the
 * edge reads it — and these two fields do not fit: with them on the flag defs
 * the bundle measures 3,679 bytes against the 3,674-byte budget asserted in
 * `packages/infra/src/web/edge.ts`. Same isolation reason
 * `edge-client-flags.ts` itself is split out of `client-flags.ts`.
 *
 * `Record<keyof typeof EdgeClientFlags, ...>` is what keeps the split from
 * becoming a drift surface: a new edge flag with no entry here is a compile
 * error, so an experiment cannot ship without stating both fields.
 *
 * See docs/projects/active/edge-experiments-account-pinned-bucketing.md §3.1.
 */
export const EdgeExperimentPolicies = {
  /**
   * `pinning: 'none'` — Mode A, the behaviour this experiment has always had.
   * Its surfaces are anonymous marketing pages only; nothing downstream of
   * signup reads the arm, so there is no account to pin to and no money
   * riding on continuity.
   */
  academicLandingFunnelV1: {
    pinning: 'none',
    exposureAt: 'academic-landing-view',
  },
  /**
   * `pinning: 'none'` — Mode A, unchanged. The arm only decides where the
   * homepage's signup CTAs point, and the experiment's denominator is
   * homepage flag exposure (see
   * `packages/infra/src/posthog/definitions/experiments/`), so exposure
   * belongs on the landing view itself rather than a post-signup event.
   */
  homepageCtaFunnelV1: {
    pinning: 'none',
    exposureAt: 'homepage-view',
  },
  /**
   * `pinning: 'at-signup'` — the first Mode C experiment, and the reason the
   * pin exists. The arm decides a charged annual price, so it must survive the
   * one moment the cookie identity ends: a visitor buckets anonymously on
   * `/pricing`, signs up, and is charged days later from a different device.
   * Freezing the cookie-derived arm onto the account at signup is what keeps
   * the price they were quoted the price they pay, and what makes a later
   * split edit unable to move a paying account (decision C2).
   *
   * `exposureAt: 'paywall-view'` — NOT the marketing pageview. `/pricing` is
   * CDN-cached and SEO-indexed; counting those renders as exposures would put
   * organic traffic that never sees a checkout into the denominator and drown
   * revenue-per-exposed in zeros (decision C5). The paywall is the first
   * surface where the arm's price is actually offered, so it is the honest
   * denominator. `exposure-policy.ts` maps this label to `deferred`, so
   * nothing fires until app code calls `fireExposure('paywall-view')` there.
   */
  pricingTestV1: {
    pinning: 'at-signup',
    exposureAt: 'paywall-view',
  },
} as const satisfies Record<keyof typeof EdgeClientFlags, ExperimentPolicy>;
